import React, { useState } from "react";
import { LiaPhoneSolid } from "react-icons/lia";
import { MdVerified } from "react-icons/md";
import UnlockContactDetailsModal from "../../modal/UnlockContactDetailsModal";
import { useNavigate } from "react-router-dom";

const ProfileCard = ({ profile, page }) => {
    const [unblockContactModal, setUnblockContactModal] = useState(false);
    const navigate = useNavigate();

    const imageUrl =
        profile?.images?.length > 0 ? profile.images[0] : "default-image-url.jpg";

    const firstname = profile?.firstname || "";
    const lastname = profile?.lastname || "";
    const name = firstname || lastname ? `${firstname} ${lastname}`.trim() : "N/A";

    const dob = profile?.profile?.date_of_birth;
    const age = dob ? new Date().getFullYear() - new Date(dob).getFullYear() : "N/A";

    // ✅ Fixed height formatting logic
    let height = "N/A";
    const heightValue = profile?.profile?.height;

    if (heightValue) {
        const [feet, inch] = heightValue.toString().split(".");

        if (inch) {
            // Handle "20" -> "2", keep "10" as "10", and "00" -> "0"
            let cleanInch = inch;

            if (inch.length === 2 && inch.endsWith("0") && inch !== "10") {
                cleanInch = inch.replace(/0+$/, "");
            }

            if (cleanInch === "" || cleanInch === "0" || cleanInch === "00") {
                // When inches are zero, show "5.0 ft"
                height = `${feet}.0 ft`;
            } else {
                height = `${feet}.${cleanInch} ft`;
            }
        } else {
            // If only feet given, show ".0"
            height = `${feet}.0 ft`;
        }
    }


    const educationValue =
        profile?.educations?.length > 0
            ? profile.educations
                .map((edu) =>
                    edu.degree
                        ? edu.university_name
                            ? `${edu.degree} (${edu.university_name})`
                            : `${edu.degree}`
                        : null
                )
                .filter(Boolean)
                .join(", ") || "N/A"
            : "N/A";

    const occupationValue =
        profile?.occupations?.length > 0
            ? profile.occupations
                .map((occ) =>
                    occ.role
                        ? occ.company_name
                            ? `${occ.role} at ${occ.company_name}`
                            : `${occ.role}`
                        : null
                )
                .filter(Boolean)
                .join(", ") || "N/A"
            : "N/A";

    const city = profile?.profile?.city || "";
    const country = profile?.profile?.country || "";
    const location =
        city || country ? `${city}${city && country ? ", " : ""}${country}` : "N/A";

    const religion = profile?.profile?.religion || "";
    const cast = profile?.profile?.cast || "";
    const castValue =
        religion || cast ? `${religion}${religion && cast ? " - " : ""}${cast}` : "N/A";

    const maritalStatus = profile?.profile?.marital_status || "N/A";

    const details = [
        { label: "Name", value: name },
        { label: "Education", value: educationValue },
        { label: "Occupation", value: occupationValue },
        { label: "Location", value: location },
        { label: "Cast", value: castValue },
        { label: "Marital Status", value: maritalStatus },
    ];

    const encodeUserId = (id) => {
        return btoa(id.toString())
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, "");
    };

    const handleCardClick = (id) => {
        const encodedId = encodeUserId(id);
        navigate(`/user/${encodedId}`);
    };

    const handleGetProfile = () => {
        setUnblockContactModal(true);
    };

    return (
        <>
            <div
                key={profile?.id}
                className="w-full bg-white rounded-xl border border-[#0000000F] p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-4 cursor-pointer transform transition-all duration-300 hover:-translate-y-1 hover:shadow-lg animate-fade-in-up"
                style={{ boxShadow: "0px 4px 20px 0px #00000014" }}
                onClick={() => handleCardClick(profile?.id)}
            >
                {/* Profile Image */}
                <div className="relative w-full md:w-48 aspect-square rounded-md w-custom-48">
                    <img
                        src={imageUrl}
                        alt={name}
                        className="object-cover object-top w-full h-full rounded-md transition-transform duration-500 hover:scale-105"
                    />


                    {/* Verification Badge */}
                    {profile?.status === "approved" && (
                        <div className="absolute bottom-2 right-2 bg-white rounded-full p-1 shadow-md">
                            <MdVerified size={24} className="text-[#e72848]" />
                        </div>
                    )}
                </div>

                {/* Profile Details */}
                <div className="flex-1">
                    <div className="space-y-1 text-base">
                        {details.map(({ label, value }) => (
                            <p className="line-clamp-2" key={label}>
                                <span className="text-[#777777] text-[15px]">{label}: </span>
                                <span className="text-[15px]">{value || "N/A"}</span>
                            </p>
                        ))}
                    </div>
                </div>

                {page !== "ContactedProfiles" ? (
                    <div className="flex sm:flex-col justify-between items-end sm:items-end sm:mt-0">
                        <div className="flex gap-2 sm:flex-col">
                            <span className="bg-[#E728481A] text-[#E72848] text-[13px] font-[500] px-2.5 py-[4px] rounded-full flex justify-center items-center">
                                {age} Year
                            </span>
                            <span className="bg-[#0A79DF1A] text-[#0A79DF] text-[13px] font-[500] px-2.5 py-[4px] rounded-full flex justify-center items-center">
                                {height}
                            </span>
                        </div>

                        {/* View Button */}
                        <button
                            className="bg-[#E72848] text-white text-[13px] font-semibold px-3.5 py-1.5 rounded-md mt-2 sm:mt-0 transition-all duration-200 transform hover:scale-[1.02] active:scale-95 shadow-md hover:shadow-lg"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCardClick(profile?.id);
                            }}
                        >
                            View
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col justify-between items-end sm:items-end mt-2 gap-2">
                        <div className="flex gap-2 sm:flex-col">
                            <span className="bg-[#E728481A] text-[#E72848] text-[13px] font-[500] px-2.5 py-[4px] rounded-full flex justify-center items-center">
                                {age} Year
                            </span>
                            <span className="bg-[#0A79DF1A] text-[#0A79DF] text-[13px] font-[500] px-2.5 py-[4px] rounded-full flex justify-center items-center">
                                {height}
                            </span>
                        </div>

                        <button className="text-[#0A79DF] text-sm font-semibold rounded-md sm:mt-2 flex items-center transition-all duration-200 transform hover:text-[#0a5cad] hover:scale-105">
                            <LiaPhoneSolid size={22} />{" "}
                            {profile?.profile?.phone_number
                                ? `${profile?.profile?.country_code || ""} ${profile?.profile?.phone_number}`
                                : "N/A"}
                        </button>
                    </div>
                )}
            </div>

            {/* Unlock Profile Modal */}
            {unblockContactModal && (
                <UnlockContactDetailsModal
                    id={profile?.id}
                    open={unblockContactModal}
                    setOpen={setUnblockContactModal}
                />
            )}
        </>
    );
};

export default ProfileCard;
